import { type RefObject, useEffect } from 'react'

import {
  RANGE_COLUMN_HEADER_ATTR,
  RANGE_DRAGGING_ATTR,
  RANGE_SELECTED_ATTR,
  type TableRangeModel,
  buildRangeTsv,
  clampRange,
  readTableRangeModel,
} from '~/components/planSubmit/tableRangeCopy/tableRangeModel'
import { writeClipboardText } from '~/components/planSubmit/tableRangeCopy/writeClipboardText'
import { type CellPos, type CellRange, makeRange } from '~/models/gridSelection'

/** ドラッグ開始とみなす移動量 (px)。 */
const DRAG_THRESHOLD_PX = 4

/** クリックが本来の機能を持つ要素 (ここからは選択を始めない)。 */
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="link"], [role="checkbox"], [role="tab"], [contenteditable="true"]'

/** 文字を編集中の要素か (Ctrl+A / Ctrl+C を奪ってよいかの判定)。 */
function isTextEntry(element: Element | null): boolean {
  if (element === null) return false
  const tag = element.tagName.toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return element.getAttribute('contenteditable') === 'true'
}

/**
 * 計画詳細の表の範囲選択 + 見出し込み Excel コピー
 * (`docs/specs/conventions.md`「Excelライクなグリッド編集」の選択・コピー、
 * `docs/specs/plan-submit/plan-common.md` §2)。React 版 mock の `TableRangeCopy` を移し、
 * 列見出しでの列の選択を足したもの。
 *
 * 閲覧コピー専用 — 貼付・編集はしない (編集グリッドは `Supply48DataGrid` / `KomaGrid` の領分)。
 *
 * 操作:
 * - セルを mousedown → drag で矩形選択 (数 px 動くまではドラッグ扱いしないので、
 *   セル内のリンク・ボタンの click と誤爆しない)
 * - `Shift+クリック` で直前の起点から矩形を拡張
 * - 見出しのセルも矩形に含められる (本文から見出しへドラッグする / 見出しからドラッグを始める)
 * - 列見出し ([[RANGE_COLUMN_HEADER_ATTR]] 付きの `<th>`) を押すとその列の全行を選択し、
 *   `Shift` を押しながら別の列見出しを押すと、最初に押した列までの複数列を選択する。
 *   列見出しを押したまま動かした時は、その見出しのセルを起点にした範囲選択になる
 * - `Ctrl+C` … 選択範囲を TSV でクリップボードへ (選んだセルだけ。見出しは選んだ時だけ入る)。未選択の時・入力欄に
 *   フォーカスがある時・画面の文字列を選んでいる時は何もしない (ブラウザ既定のコピーに任せる)
 * - `Ctrl+A` … その表にマウスで触れている時だけ、全行全列を選択 (mock と同じ。spec には無い)
 * - `Escape` … 選択解除 (mock と同じ。spec には無い)
 *
 * 選択状態は DOM 属性 ([[RANGE_SELECTED_ATTR]]) で持ち、React state を経由しない
 * (48 行 × N 列の再描画を誘発しない)。
 *
 * @param tableRef 対象の `<table>` への ref (mount 時に描画済みであること)
 */
export function useTableRangeCopy(
  tableRef: RefObject<HTMLTableElement | null>,
): void {
  useEffect(() => {
    const table = tableRef.current
    if (table === null) return undefined
    const doc = table.ownerDocument
    const win = doc.defaultView
    if (win === null) return undefined

    /**
     * 直近にこの表を操作した時のモデル (null = 表の外を押した後で、Ctrl+A の対象外)。
     * ドラッグ中は mousemove のたびに DOM を読み直さないよう使い回す。
     */
    let activeModel: TableRangeModel | null = null
    /** Shift+クリックの起点。 */
    let anchor: CellPos | null = null
    /** 列見出しで選んだ時の起点の列 (Shift+列見出しはこの列から広げる)。 */
    let columnAnchor: number | null = null
    let range: CellRange | null = null
    let dragOrigin: { x: number; y: number; pos: CellPos } | null = null
    let isDragging = false

    const asElement = (value: EventTarget | null): Element | null =>
      value !== null && typeof (value as Element).closest === 'function'
        ? (value as Element)
        : null

    /**
     * 表のセル・列見出しを押した時のブラウザ既定の動作 (文字の間に入力位置 = 点滅する
     * カーソルを置く・文字選択を始める) を止める。読取専用の表なので要らない。止めると
     * フォーカスが元の入力欄に残り、直後の Ctrl+C がそちらのコピー扱いになるので外しておく
     */
    const takeOverMouseDown = (event: MouseEvent): void => {
      event.preventDefault()
      win.getSelection()?.removeAllRanges()
      const focused = doc.activeElement
      if (focused instanceof win.HTMLElement && focused !== doc.body) {
        focused.blur()
      }
    }

    const clearSelection = (): void => {
      range = null
      for (const el of Array.from(
        table.querySelectorAll(`[${RANGE_SELECTED_ATTR}]`),
      )) {
        el.removeAttribute(RANGE_SELECTED_ATTR)
      }
    }

    /** 表の外を押した時など、この表の操作をやめる。 */
    const deactivate = (): void => {
      activeModel = null
      anchor = null
      columnAnchor = null
      clearSelection()
    }

    const applyRange = (model: TableRangeModel, next: CellRange): void => {
      clearSelection()
      range = next
      for (let row = next.rowMin; row <= next.rowMax; row += 1) {
        for (let col = next.colMin; col <= next.colMax; col += 1) {
          model.grid[row]?.[col]?.cell.setAttribute(RANGE_SELECTED_ATTR, 'true')
        }
      }
    }

    /** セル要素から表の何行何列かを解決する (表の外・対象外なら null)。 */
    const resolveCell = (
      cell: Element,
      model?: TableRangeModel,
    ): { model: TableRangeModel; pos: CellPos } | null => {
      if (!table.contains(cell)) return null
      const resolved = model ?? readTableRangeModel(table)
      const pos = resolved.posOf(cell)
      if (pos === null || pos.col >= resolved.colCount) return null
      return { model: resolved, pos }
    }

    const onMouseMove = (event: MouseEvent): void => {
      const origin = dragOrigin
      if (origin === null || activeModel === null) return
      if (!isDragging) {
        // 数 px 動くまではドラッグ扱いしない (リンク・ボタンの click を殺さない)
        if (
          Math.abs(event.clientX - origin.x) < DRAG_THRESHOLD_PX &&
          Math.abs(event.clientY - origin.y) < DRAG_THRESHOLD_PX
        ) {
          return
        }
        isDragging = true
        table.setAttribute(RANGE_DRAGGING_ATTR, 'true')
        // 列見出しから動かし始めた時は列の選択をやめ、その見出しのセルを起点にした範囲選択に
        // 切り替える (見出しを含めて選べる)。以後の Shift+クリックもこの起点から広げる
        anchor = origin.pos
        columnAnchor = null
      }
      const cell = asElement(event.target)?.closest('td, th') ?? null
      if (cell === null) return
      const resolved = resolveCell(cell, activeModel)
      // 表の外へはみ出した時は直前の範囲を保つ
      if (resolved === null) return
      event.preventDefault()
      win.getSelection()?.removeAllRanges()
      applyRange(resolved.model, makeRange(origin.pos, resolved.pos))
    }

    const stopDrag = (): void => {
      dragOrigin = null
      if (isDragging) {
        isDragging = false
        table.removeAttribute(RANGE_DRAGGING_ATTR)
      }
      doc.removeEventListener('mousemove', onMouseMove)
      doc.removeEventListener('mouseup', stopDrag)
    }

    const startDrag = (event: MouseEvent, pos: CellPos): void => {
      dragOrigin = { x: event.clientX, y: event.clientY, pos }
      doc.addEventListener('mousemove', onMouseMove)
      doc.addEventListener('mouseup', stopDrag)
    }

    /**
     * 列見出しの mousedown (plan-common §2)。その列の全行 (列の選択に含める行) を選ぶ。
     * 処理したら true。
     */
    const selectColumnFromHeader = (
      header: Element,
      event: MouseEvent,
    ): boolean => {
      const model = readTableRangeModel(table)
      const col = model.columnOf(header)
      if (col === null || model.columnRows === null) return false
      takeOverMouseDown(event)
      const fromCol =
        event.shiftKey && columnAnchor !== null ? columnAnchor : col
      activeModel = model
      columnAnchor = fromCol
      anchor = { row: model.columnRows.rowMin, col: fromCol }
      applyRange(model, {
        rowMin: model.columnRows.rowMin,
        rowMax: model.columnRows.rowMax,
        colMin: Math.min(fromCol, col),
        colMax: Math.max(fromCol, col),
      })
      return true
    }

    const onMouseDown = (event: MouseEvent): void => {
      if (event.button !== 0) return
      const target = asElement(event.target)
      if (target === null || !table.contains(target)) {
        // 表の外をクリックしたら選択を解除する (他の表・フィルタへ移った)
        deactivate()
        return
      }
      // リンク・ボタン・入力の上からは選択を始めない (本来の click / focus を優先)
      if (target.closest(INTERACTIVE_SELECTOR) !== null) {
        clearSelection()
        return
      }
      const header = target.closest(`th[${RANGE_COLUMN_HEADER_ATTR}]`)
      if (header !== null && selectColumnFromHeader(header, event)) {
        // 押して離すだけなら列の選択のまま。押したまま動かしたら範囲選択へ (onMouseMove)
        const origin = event.shiftKey
          ? null
          : resolveCell(header, activeModel ?? undefined)
        if (origin !== null) startDrag(event, origin.pos)
        return
      }
      const cell = target.closest('td, th')
      const resolved = cell === null ? null : resolveCell(cell)
      if (resolved === null) {
        deactivate()
        return
      }
      activeModel = resolved.model
      columnAnchor = null
      takeOverMouseDown(event)
      if (event.shiftKey && anchor !== null) {
        applyRange(resolved.model, makeRange(anchor, resolved.pos))
        return
      }
      anchor = resolved.pos
      clearSelection()
      startDrag(event, resolved.pos)
    }

    const copySelection = (): boolean => {
      if (range === null) return false
      // 選択後に行・列が入れ替わっている可能性があるので読み直してから切り出す
      const model = readTableRangeModel(table)
      const clamped = clampRange(range, model)
      if (clamped === null) return false
      const tsv = buildRangeTsv(model, clamped)
      if (tsv === '') return false
      void writeClipboardText(tsv, doc)
      return true
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return
      if (event.key === 'Escape') {
        if (range !== null) clearSelection()
        return
      }
      if (!event.ctrlKey && !event.metaKey) return
      const key = event.key.toLowerCase()
      if (key === 'c') {
        // 入力欄にフォーカスがある時・画面の文字列を選んでいる時は、表の選択が残っていても
        // そちらのコピーを優先する (表の TSV で上書きしない。プロトタイプと同じ)
        if (isTextEntry(doc.activeElement)) return
        const textSelection = win.getSelection()
        if (textSelection !== null && !textSelection.isCollapsed) return
        // 未選択時は何もしない = ブラウザ既定のテキストコピーがそのまま効く
        if (!copySelection()) return
        event.preventDefault()
        return
      }
      if (key !== 'a') return
      if (activeModel === null) return
      // 入力欄で文字を編集している時の Ctrl+A は奪わない
      if (isTextEntry(doc.activeElement)) return
      const model = readTableRangeModel(table)
      if (model.rowCount === 0 || model.colCount === 0) return
      event.preventDefault()
      activeModel = model
      anchor = { row: 0, col: 0 }
      columnAnchor = null
      applyRange(model, {
        rowMin: 0,
        rowMax: model.rowCount - 1,
        colMin: 0,
        colMax: model.colCount - 1,
      })
    }

    /**
     * HTML5 ドラッグが始まったら矩形選択のドラッグを中断する。ネイティブドラッグ中は
     * `mouseup` が**発火しない**ため、放置するとボタンを離した後も矩形が追従し続ける。
     */
    const onNativeDragStart = (): void => {
      stopDrag()
      clearSelection()
    }

    doc.addEventListener('mousedown', onMouseDown)
    doc.addEventListener('keydown', onKeyDown)
    doc.addEventListener('dragstart', onNativeDragStart)
    return () => {
      doc.removeEventListener('mousedown', onMouseDown)
      doc.removeEventListener('keydown', onKeyDown)
      doc.removeEventListener('dragstart', onNativeDragStart)
      stopDrag()
      clearSelection()
    }
  }, [tableRef])
}
